import React from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { TableCell, TableRow } from '@/components/ui/table';
import { ArrowRight, MessageCircle, Users } from 'lucide-react';
import { OtrosTramiteActions } from './OtrosTramiteActions';
import OtroTramiteIndicators from './OtroTramiteIndicators';
import { ArchivosButton } from './ArchivosButton';
import { formatDate } from '../utils';
import { useOtrosTramitesColumnVisibility } from '../contexts/OtrosTramitesColumnVisibilityContext';
import type { OtroTramiteRecord } from '../types';

export interface OtrosTramiteRowProps {
  tramite: OtroTramiteRecord;
  onView: (tramite: OtroTramiteRecord) => void;
  onEdit: (tramite: OtroTramiteRecord) => void;
  onDelete: (tramite: OtroTramiteRecord) => void;
  onRetirar: (tramite: OtroTramiteRecord) => void;
  onViewRetiroHistorial?: (tramite: OtroTramiteRecord) => void;
  onManageCobros?: (tramite: OtroTramiteRecord) => void;
  onManageFacturas?: (tramite: OtroTramiteRecord) => void;
  onManageArchivos?: (tramite: OtroTramiteRecord) => void;
  onSendWhatsAppRetiro?: (tramite: OtroTramiteRecord) => void;
  onViewOtorgantes?: (tramite: OtroTramiteRecord) => void;
  onViewResponsables?: (tramite: OtroTramiteRecord) => void;
  onViewGarantes?: (tramite: OtroTramiteRecord) => void;
  onEditAssignedUser?: (tramite: OtroTramiteRecord) => void;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
}

export const formatPersonasLabel = (personas: Array<{ nombre: string }>): string => {
  if (personas.length === 0) {
    return '-';
  }

  const [firstPersona] = personas;
  const firstName = (firstPersona?.nombre ?? '').trim();
  if (personas.length === 1) {
    return firstName || '-';
  }

  const othersCount = personas.length - 1;
  return `${firstName || '-'} (+${othersCount})`;
};

export const formatAssignedUsersLabel = (users: Array<{ nombre: string }>): string => {
  if (users.length === 0) {
    return '-';
  }

  return users.map((user) => user.nombre).join(', ');
};

export const OtrosTramiteRow: React.FC<OtrosTramiteRowProps> = ({
  tramite,
  onView,
  onEdit,
  onDelete,
  onRetirar,
  onViewRetiroHistorial,
  onManageCobros,
  onManageFacturas,
  onManageArchivos,
  onSendWhatsAppRetiro,
  onViewOtorgantes,
  onViewResponsables,
  onViewGarantes,
  onEditAssignedUser,
  isSelected,
  onToggleSelect,
}) => {
  const { isColumnVisible } = useOtrosTramitesColumnVisibility();
  const detalle = (tramite.detalle ?? '').trim();
  const detalleDisplay = detalle.length > 80 ? `${detalle.slice(0, 80)}...` : detalle;
  const copiasDisponibles = tramite.copiasTotales - tramite.copiasActuales;
  const puedeRetirar = copiasDisponibles > 0;
  const retiroCompleto = !puedeRetirar;
  const otorgantesLabel = formatPersonasLabel(tramite.otorgantes);
  const responsablesLabel = formatPersonasLabel(tramite.responsables);
  const garantesLabel = formatPersonasLabel(tramite.garantes ?? []);
  const assignedUsersLabel = formatAssignedUsersLabel(tramite.assignedUsers ?? []);

  return (
    <TableRow className={`border-b border-gray-100 hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
      <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(tramite.id)}
          disabled={!puedeRetirar}
          aria-label={`Seleccionar trámite ${tramite.id}`}
        />
      </TableCell>
      {isColumnVisible('id') && (
        <TableCell className="font-medium">
          {tramite.id}
        </TableCell>
      )}
      {isColumnVisible('fecha') && (
        <TableCell>{formatDate(tramite.fecha)}</TableCell>
      )}
      {isColumnVisible('otorgantes') && (
        <TableCell
          className={tramite.otorgantesCount > 0 ? 'cursor-pointer hover:bg-gray-100 transition-colors group' : ''}
          onClick={() => tramite.otorgantesCount > 0 && onViewOtorgantes?.(tramite)}
        >
          <span className={`inline-flex items-center gap-2 ${tramite.otorgantesCount > 0 ? 'group-hover:underline' : ''}`}>
            <Users className="h-4 w-4" />
            {otorgantesLabel}
          </span>
        </TableCell>
      )}
      {isColumnVisible('responsables') && (
        <TableCell
          className={tramite.responsablesCount > 0 ? 'cursor-pointer hover:bg-gray-100 transition-colors group' : ''}
          onClick={() => tramite.responsablesCount > 0 && onViewResponsables?.(tramite)}
        >
          <span className={`inline-flex items-center gap-2 ${tramite.responsablesCount > 0 ? 'group-hover:underline' : ''}`}>
            <Users className="h-4 w-4" />
            {responsablesLabel}
          </span>
        </TableCell>
      )}
      {isColumnVisible('garantes') && (
        <TableCell
          className={(tramite.garantesCount ?? 0) > 0 ? 'cursor-pointer hover:bg-gray-100 transition-colors group' : ''}
          onClick={() => (tramite.garantesCount ?? 0) > 0 && onViewGarantes?.(tramite)}
        >
          <span className={`inline-flex items-center gap-2 ${(tramite.garantesCount ?? 0) > 0 ? 'group-hover:underline' : ''}`}>
            <Users className="h-4 w-4" />
            {garantesLabel}
          </span>
        </TableCell>
      )}
      {isColumnVisible('asignados') && (
        <TableCell title={assignedUsersLabel}>
          {onEditAssignedUser ? (
            <button
              type="button"
              className="block max-w-[220px] truncate text-left text-primary hover:underline"
              title={assignedUsersLabel === '-' ? 'Asignar responsable' : assignedUsersLabel}
              onClick={() => onEditAssignedUser(tramite)}
            >
              {assignedUsersLabel === '-' ? 'Asignar' : assignedUsersLabel}
            </button>
          ) : (
            <span className="block max-w-[220px] truncate">
              {assignedUsersLabel}
            </span>
          )}
        </TableCell>
      )}
      {isColumnVisible('tipo') && (
        <TableCell>{tramite.tipo}</TableCell>
      )}
      {isColumnVisible('detalle') && (
        <TableCell title={detalle || undefined}>
          <span className="block max-w-[260px] truncate">
            {detalle ? detalleDisplay : '-'}
          </span>
        </TableCell>
      )}
      {isColumnVisible('copias') && (
        <TableCell
          className={retiroCompleto ? 'cursor-pointer hover:bg-gray-100 transition-colors group' : undefined}
          onClick={() => retiroCompleto && onViewRetiroHistorial?.(tramite)}
        >
          <div className="grid grid-cols-[48px_minmax(76px,1fr)_32px] items-center gap-3">
            <span className="font-medium inline-block min-w-[40px] text-center">{`${tramite.copiasActuales}/${tramite.copiasTotales}`}</span>
            {puedeRetirar ? (
              <Button
                variant="link"
                size="xs"
                className="w-[92px] justify-start justify-self-start p-0 text-primary whitespace-nowrap"
                onClick={(event) => {
                  event.stopPropagation();
                  onRetirar(tramite);
                }}
              >
                <ArrowRight className="h-3 w-3" />
                Retirar
              </Button>
            ) : (
              <span
                className="inline-flex w-[92px] items-center gap-2 justify-self-start whitespace-nowrap text-md font-medium text-gray-400 group-hover:underline"
                title="No hay copias disponibles"
              >
                <ArrowRight className="h-3 w-3 opacity-0" />
                Completo
              </span>
            )}
            {onSendWhatsAppRetiro && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 justify-self-center p-0 text-green-600 hover:text-green-700"
                onClick={(event) => {
                  event.stopPropagation();
                  onSendWhatsAppRetiro(tramite);
                }}
                title="Enviar WhatsApp retiro"
              >
                <MessageCircle className="h-4 w-4" />
                <span className="sr-only">Enviar WhatsApp retiro</span>
              </Button>
            )}
          </div>
        </TableCell>
      )}
      {isColumnVisible('factura') && (
        <TableCell>
          {onManageFacturas && (
            <ArchivosButton
              tramite={tramite}
              onClick={onManageFacturas}
              label="Factura"
              count={tramite.facturasCount ?? 0}
            />
          )}
        </TableCell>
      )}
      {isColumnVisible('archivos') && (
        <TableCell>
          {onManageArchivos && (
            <ArchivosButton
              tramite={tramite}
              onClick={onManageArchivos}
              label="Archivos"
              count={tramite.archivosCount ?? 0}
            />
          )}
        </TableCell>
      )}
      {isColumnVisible('indicadores') && (
        <TableCell>
          <OtroTramiteIndicators tramite={tramite} onManageCobros={onManageCobros} />
        </TableCell>
      )}
      {isColumnVisible('acciones') && (
        <OtrosTramiteActions
          tramite={tramite}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onManageCobros={onManageCobros}
        />
      )}
    </TableRow>
  );
};
